import React, { useState } from 'react';
import {
  FaBuilding,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCopy,
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';

/**
 * Create an NBN customer outside the public signup flow.
 *
 * WHY: until 2026-09-24 signup was the ONLY thing that could create a PeaknetCoreNBN
 * record. Business customers who negotiate terms, already hold a LOC/AVC, or simply
 * won't self-serve had nowhere to live — so they got invoiced as "IT services" with no
 * service record, no provisioning history and no CIS/consent trail.
 *
 * ⚠️ This does NOT start billing. The customer is created with `billingStatus` unset,
 * which is the single field recurring billing selects on. They stay invisible to the
 * billing run until someone approves them — deliberately, so nobody is charged before a
 * payment method exists or before the service is actually live.
 */

const FIELD = 'w-full px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none';
const LABEL = 'block text-xs font-semibold text-gray-700 mb-1';

const NewBusinessCustomer = ({ onCreated }) => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', companyName: '', email: '', phone: '',
    address: '', suburb: '', state: 'NSW', postcode: '',
    plan: '', monthlyFee: '',
    connectionType: 'transfer', avcId: '', locId: '',
    serviceActivationDate: '', notes: '',
    sendPasswordSetup: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Mirrors the server-side pro-rata formula in lambda-admin-customer-management.js:
  //   remainingDays = daysInMonth - activationDay + 1   (activation day inclusive)
  //   amount        = monthlyFee / daysInMonth * remainingDays   (GST-INCLUSIVE)
  // Shown here only so you can sanity-check the quote amount before sending it.
  const proRata = (() => {
    const fee = parseFloat(form.monthlyFee);
    if (!form.serviceActivationDate || !Number.isFinite(fee) || fee <= 0) return null;
    const d = new Date(`${form.serviceActivationDate}T00:00:00`);
    if (isNaN(d.getTime())) return null;
    const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const remainingDays = daysInMonth - d.getDate() + 1;
    const amount = Math.round((fee / daysInMonth) * remainingDays * 100) / 100;
    const firstFull = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    return {
      daysInMonth,
      remainingDays,
      amount,
      gst: Math.round((amount / 11) * 100) / 100, // GST-inclusive → /11, never *0.1
      firstFullBillingDate: firstFull.toISOString().split('T')[0],
    };
  })();

  const isTransfer = form.connectionType === 'transfer';
  // The carrier verifies the AVC against the LOC on a TRANSFER — a malformed one fails at order
  // time, so catch the shape here. Same rule as ProvisionTool: AVC + 12 digits.
  const avcValid = !form.avcId || /^AVC\d{12}$/i.test(form.avcId.trim());
  // The LOC had no validation until a real order was saved as "LLOC0001..." — a typo
  // that only surfaces when provisioning fails, long after the customer has paid.
  const locValid = !form.locId || /^LOC\d{12}$/i.test(form.locId.trim());

  const canSubmit =
    form.firstName.trim() && form.lastName.trim() && form.email.includes('@') &&
    form.address.trim() && form.postcode.trim() && form.plan.trim() &&
    parseFloat(form.monthlyFee) > 0 && avcValid && locValid &&
    (!isTransfer || form.avcId.trim());

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          action: 'createNbnCustomer',
          ...form,
          monthlyFee: parseFloat(form.monthlyFee),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      setResult({ ...data, proRata });
      if (onCreated) onCreated(data);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <FaCheckCircle className="text-green-600 text-2xl mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-green-900 text-lg">Customer created</h3>
              <p className="text-green-800 text-sm mt-1">
                {result.customer?.fullName} — account {result.accountNumber}
              </p>
              <p className="text-green-700 text-xs mt-2">
                {result.passwordEmailSent
                  ? 'A password-setup email has been sent so they can access the portal.'
                  : '⚠️ The password-setup email did not send — use "Send password reset" on their record.'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-amber-900 text-sm font-semibold mb-1">Not billing yet</p>
          <p className="text-amber-800 text-xs">
            This customer is invisible to the recurring billing run until you approve them.
            Approve only once their payment method is on file and the service is live.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">Next steps</p>
          <ol className="text-sm text-gray-800 space-y-3 list-decimal list-inside">
            <li>
              Create the quote with this UID so the consents attach to the customer:
              <div className="flex items-center gap-2 mt-1">
                <code className="block bg-gray-100 px-2 py-1 rounded text-xs break-all flex-1">{result.UID}</code>
                <button
                  onClick={() => navigator.clipboard?.writeText(result.UID)}
                  className="text-gray-500 hover:text-gray-800 p-1"
                  title="Copy UID"
                >
                  <FaCopy />
                </button>
              </div>
              <span className="text-xs text-gray-600">
                Set service type <strong>nbn® Service (business)</strong>, payment type{' '}
                <strong>one-time</strong>
                {result.proRata ? <> for <strong>${result.proRata.amount.toFixed(2)}</strong></> : null}.
              </span>
            </li>
            <li>Send the quote link — they accept the nbn® terms and pay, which saves their card.</li>
            <li>Attach their Stripe customer + payment method to this record, then approve to start billing.</li>
            <li>Provision: ProvisionTool → load this customer → TRANSFER → Manual skip → enter LOC/AVC.</li>
          </ol>
        </div>

        <button
          onClick={() => { setResult(null); setForm((p) => ({ ...p, firstName: '', lastName: '', companyName: '', email: '', phone: '', avcId: '', locId: '' })); }}
          className="mt-6 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-gray-700"
        >
          Create another
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <FaBuilding className="text-2xl text-blue-600" />
        <h2 className="text-xl font-bold text-gray-900">New business nbn® customer</h2>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        For orders that don&apos;t go through public signup. Creates the customer record and a
        portal login, but does <strong>not</strong> start billing or place any carrier order.
      </p>

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <FaExclamationTriangle className="text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      ) : null}

      <div className="space-y-6">
        <section className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-4">Customer</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>First name *</label>
              <input className={FIELD} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Last name *</label>
              <input className={FIELD} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Business name</label>
              <input className={FIELD} value={form.companyName} onChange={(e) => set('companyName', e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Email *</label>
              <input className={FIELD} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
              <p className="text-[11px] text-gray-500 mt-1">Their portal login and where the quote goes.</p>
            </div>
            <div>
              <label className={LABEL}>Phone</label>
              <input className={FIELD} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-4">Service address</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={LABEL}>Full address *</label>
              <input className={FIELD} value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="12 Example St, Sydney NSW 2000" />
            </div>
            <div>
              <label className={LABEL}>Suburb</label>
              <input className={FIELD} value={form.suburb} onChange={(e) => set('suburb', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>State</label>
                <select className={FIELD} value={form.state} onChange={(e) => set('state', e.target.value)}>
                  {['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL}>Postcode *</label>
                <input className={FIELD} value={form.postcode} onChange={(e) => set('postcode', e.target.value)} />
              </div>
            </div>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">
            The carrier uses the postcode as LocationReference when provisioning — an order can&apos;t be
            placed without it.
          </p>
        </section>

        <section className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-4">Plan &amp; pricing</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Plan name *</label>
              <input className={FIELD} value={form.plan} onChange={(e) => set('plan', e.target.value)} placeholder="nbn 100/40" />
            </div>
            <div>
              <label className={LABEL}>Monthly fee (GST inc.) *</label>
              <input className={FIELD} type="number" step="0.01" value={form.monthlyFee} onChange={(e) => set('monthlyFee', e.target.value)} placeholder="110.00" />
            </div>
            <div>
              <label className={LABEL}>Service activation date</label>
              <input className={FIELD} type="date" value={form.serviceActivationDate} onChange={(e) => set('serviceActivationDate', e.target.value)} />
            </div>
          </div>

          {proRata ? (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                Pro-rata: <strong>${proRata.amount.toFixed(2)}</strong> for {proRata.remainingDays} day
                {proRata.remainingDays === 1 ? '' : 's'} (of {proRata.daysInMonth}) — GST ${proRata.gst.toFixed(2)}
              </p>
              <p className="text-xs text-blue-800 mt-1">
                Quote this amount as a one-time charge. Full ${parseFloat(form.monthlyFee).toFixed(2)}/month
                then bills from {proRata.firstFullBillingDate}.
              </p>
            </div>
          ) : null}
        </section>

        <section className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-4">Connection</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Type</label>
              <select className={FIELD} value={form.connectionType} onChange={(e) => set('connectionType', e.target.value)}>
                <option value="transfer">Transfer (existing service)</option>
                <option value="new">New connection</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>AVC ID {isTransfer ? '*' : ''}</label>
              <input
                className={`${FIELD} ${form.avcId && !avcValid ? 'border-red-400' : ''}`}
                value={form.avcId}
                onChange={(e) => set('avcId', e.target.value.toUpperCase())}
                placeholder="AVC000012345678"
              />
              {form.avcId && !avcValid ? (
                <p className="text-[11px] text-red-600 mt-1">Must be AVC followed by 12 digits.</p>
              ) : null}
            </div>
            <div>
              <label className={LABEL}>LOC ID</label>
              <input
                className={`${FIELD} ${form.locId && !locValid ? 'border-red-400' : ''}`}
                value={form.locId}
                onChange={(e) => set('locId', e.target.value.toUpperCase())}
                placeholder="LOC000012345678"
              />
              {form.locId && !locValid ? (
                <p className="text-[11px] text-red-600 mt-1">Must be LOC followed by 12 digits.</p>
              ) : null}
            </div>
          </div>
          {isTransfer ? (
            <p className="text-[11px] text-gray-500 mt-2">
              The carrier verifies the AVC against the LOC. With both on file you can skip qualify in
              the Provision tool and go straight to the transfer order.
            </p>
          ) : null}
        </section>

        <section className="bg-white border border-gray-200 rounded-xl p-5">
          <label className={LABEL}>Notes</label>
          <textarea className={FIELD} rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          <label className="flex items-center gap-2 mt-4 cursor-pointer">
            <input type="checkbox" checked={form.sendPasswordSetup} onChange={(e) => set('sendPasswordSetup', e.target.checked)} className="w-4 h-4" />
            <span className="text-sm text-gray-700">Email them a link to set their portal password</span>
          </label>
        </section>

        <button
          onClick={submit}
          disabled={!canSubmit || submitting}
          className="w-full py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? <><FaSpinner className="animate-spin" /> Creating…</> : 'Create customer'}
        </button>
        <p className="text-[11px] text-center text-gray-500 -mt-3">
          Creates the record and login only. No billing, no carrier order.
        </p>
      </div>
    </div>
  );
};

export default NewBusinessCustomer;
